import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class I18nService {
	private readonly translate = inject(TranslateService);
	private readonly languageSubject = new BehaviorSubject<string>('en');
	public currentLanguage$ = this.languageSubject.asObservable();

	constructor() {
		// Update language subject when translate service changes language
		this.translate.onLangChange.subscribe((event) => {
			this.languageSubject.next(event.lang);
		});

		// Initialize subject with current language
		setTimeout(() => {
			this.languageSubject.next(this.translate.currentLang || this.translate.getDefaultLang());
		});
	}

	/**
	 * Change the current language
	 * @param lang Language code to switch to
	 */
	changeLanguage(lang: string): void {
		if (['en', 'de'].includes(lang)) {
			this.translate.use(lang);
			localStorage.setItem('selectedLanguage', lang);
		}
	}

	/**
	 * Get the current active language
	 * @returns Current language code
	 */
	getCurrentLanguage(): string {
		return this.translate.currentLang || this.translate.getDefaultLang();
	}

	/**
	 * Get the current active language with full name
	 * @returns Current language full name (e.g., 'English', 'Deutsch')
	 */
	getCurrentLanguageLong(): string {
		const currentLang = this.getCurrentLanguage();
		switch (currentLang) {
			case 'en':
				return 'English';
			case 'de':
				return 'Deutsch';
			default:
				return 'English';
		}
	}

	/**
	 * Check if the given language is the current active one
	 * @param lang Language code to check
	 * @returns True if it's the current language
	 */
	isCurrentLanguage(lang: string): boolean {
		return this.getCurrentLanguage() === lang;
	}
}
